import { useState, useEffect } from "react"
import styled from "styled-components"
import { format, differenceInCalendarDays } from "date-fns"
import { ko } from "date-fns/locale"
import toast from "react-hot-toast"
import Calendar from "./calendar"
import { Plus, Trash, CalendarIcon, ArrowUp, ArrowDown, X, ListChecks, HelpCircle, Download, FolderOpen } from "lucide-react" // ArrowUp, ArrowDown, X, ListChecks, HelpCircle 추가
import { confirmDialog } from "../../utils/confirmDialog"
import { getTaskStatus, STATUS } from "../../utils/taskStatus"

// 사이클 감지: candidatePredecessorId가 currentTaskId의 (간접) 후행이면 사이클
const wouldCreateCycle = (allTasks, currentTaskId, candidatePredecessorId) => {
  if (currentTaskId === candidatePredecessorId) return true
  const byId = new Map(allTasks.map((t) => [t.id, t]))
  const visited = new Set()
  const stack = [candidatePredecessorId]
  while (stack.length) {
    const id = stack.pop()
    if (visited.has(id)) continue
    visited.add(id)
    const t = byId.get(id)
    if (!t) continue
    const deps = t.dependencies || []
    if (deps.includes(currentTaskId)) return true
    for (const d of deps) stack.push(d)
  }
  return false
}

// 기존 styled components 아래에 추가
const OrderButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-left: 8px;
`;

const OrderButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 2px;
  color: #6b7280;
  
  &:hover:not(:disabled) {
    background-color: #f3f4f6;
    color: #374151;
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;


// 기존 스타일에서 width와 border-right 제거
const SidebarContainer = styled.div`

  background-color: white;
  display: flex;
  flex-direction: column;
  height: 100%; /* 추가 */
`

const SidebarHeader = styled.div`
  padding: 12px;
  background: white;
  border-bottom: 1px solid #f1f5f9;
`

const BacklogLinkRow = styled.div`
  padding: 8px 12px 0;
`

const SidebarFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  border-top: 1px solid #f1f5f9;
  background: white;
  flex-shrink: 0;
`

const FooterButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 7px 10px;
  font-size: 12.5px;
  font-weight: 600;
  color: #475569;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  letter-spacing: -0.01em;

  svg { color: #64748b; transition: color 0.12s; }

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #0f172a;
    svg { color: #2563eb; }
  }
`

const BacklogLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px;
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  color: #1d4ed8;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  letter-spacing: -0.01em;
  transition: background 0.12s, color 0.12s;

  svg { color: #2563eb; }

  &:hover {
    background: #eff6ff;
    color: #1e40af;
  }
`

const PrimaryCTA = styled.button`
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 16px;
  font-size: 15px;
  font-weight: 700;
  color: white;
  background: #2563eb;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.12s, transform 0.04s;
  letter-spacing: -0.01em;

  &:hover { background: #1d4ed8; }
  &:active { background: #1e40af; transform: translateY(1px); }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.28);
  }
`

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  background: white;
  padding: 0 6px;
  gap: 4px;
`

const Tab = styled.button`
  flex: 1;
  position: relative;
  padding: 12px 6px 13px;
  background-color: transparent;
  border: none;
  font-size: 14px;
  font-weight: ${(props) => (props.active ? 700 : 500)};
  color: ${(props) => (props.active ? "#2563eb" : "#64748b")};
  cursor: pointer;
  transition: color 0.15s;

  &::after {
    content: "";
    position: absolute;
    left: 10px;
    right: 10px;
    bottom: -1px;
    height: 2.5px;
    border-radius: 2px 2px 0 0;
    background: ${(props) => (props.active ? "#2563eb" : "transparent")};
  }

  &:hover {
    color: ${(props) => (props.active ? "#2563eb" : "#0f172a")};
  }
`

const ContentContainer = styled.div`
  flex: 2; /* 1에서 2로 증가 */
  overflow: auto;
  min-height: 300px; /* 최소 높이 설정 */
`

const TaskList = styled.div`
  flex: 1;
  overflow: auto;
`

const TaskItem = styled.div`
  padding: 9px 10px 10px;
  margin: 4px 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f9fafb;
  }
  
  ${(props) =>
    props.isSelected &&
    `
    background-color: #f3f4f6;
  `}
`

const TaskHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const TaskColor = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
`

const TaskName = styled.div`
  font-weight: 500;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 8px;
  font-size: 10.5px;
  font-weight: 700;
  border-radius: 999px;
  background: ${(p) => p.bg};
  color: ${(p) => p.color};
  border: 1px solid ${(p) => p.border};
  white-space: nowrap;
  flex-shrink: 0;
`

const TaskDate = styled.div`
  font-size: 11.5px;
  color: #6b7280;
  margin-top: 3px;
`

const NoteButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 22px;
  padding: 0 8px;
  font-size: 11px;
  font-weight: 600;
  color: #1d4ed8;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  flex-shrink: 0;

  svg { color: #2563eb; }

  &:hover {
    background: #dbeafe;
    border-color: #93c5fd;
    color: #1e40af;
  }
`

const TaskProgressBar = styled.div`
  width: 100%;
  height: 5px;
  background-color: #e5e7eb;
  border-radius: 9999px;
  margin-top: 6px;
  position: relative;
  overflow: hidden;
`

const TaskProgress = styled.div`
  height: 6px;
  border-radius: 9999px;
  background-color: #2563eb;
`

const UserAvatar = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 1.5px solid white;
  box-shadow: 0 0 0 1px #e2e8f0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`

const MilestoneItem = styled.div`
  padding: 12px;
  margin: 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 12px;
  
  &:hover {
    background-color: #f9fafb;
  }
  
  ${(props) =>
    props.isSelected &&
    `
    background-color: #f3f4f6;
  `}
`

const MilestoneMarker = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 12px;
`

const MilestoneInfo = styled.div`
  flex: 1;
`

const MilestoneName = styled.div`
  font-weight: 500;
`

const MilestoneDate = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
`

const TaskDetails = styled.div`
  border-top: 1px solid #e5e7eb;
  padding: 16px;
  max-height: 400px; /* 최대 높이 제한 추가 */
  overflow-y: auto; /* 스크롤 가능하게 */
  flex-shrink: 0; /* 축소 방지 */
`

const TaskDetailsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`

const TaskDetailsTitle = styled.h3`
  font-weight: 600;
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const HeaderDivider = styled.span`
  width: 1px;
  height: 18px;
  background: #e5e7eb;
  margin: 0 4px;
`

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  border: none;
  background-color: transparent;
  cursor: pointer;
  color: #6b7280;

  &:hover {
    background-color: #f3f4f6;
    color: #111827;
  }
`

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  border: none;
  background-color: transparent;
  cursor: pointer;

  &:hover {
    background-color: #fee2e2;
  }
  
  svg {
    color: #ef4444;
  }
`

const FormGroup = styled.div`
  margin-bottom: 16px;
`

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
`

const Input = styled.input`
  width: calc(100% - 24px);
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 1px #2563eb;
  }
`

const SliderContainer = styled.div`
  width: 100%;
`

const Slider = styled.input.attrs({ type: "range" })`
  width: 100%;
  height: 6px;
  -webkit-appearance: none;
  background: #e5e7eb;
  border-radius: 9999px;
  outline: none;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #2563eb;
    cursor: pointer;
  }
`

const NumberInput = styled.input.attrs({ type: "number" })`
  width: 72px;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  color: #1f2937;
  background-color: white;
  text-align: center;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 1px #2563eb;
  }
`

const ColorPicker = styled.div`
  display: flex;
  gap: 8px;
`

const ColorOption = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  
  ${(props) =>
    props.isSelected &&
    `
    box-shadow: 0 0 0 2px white, 0 0 0 4px #2563eb;
  `}
`

const UserSelector = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const UserOption = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  
  ${(props) =>
    props.isSelected &&
    `
    .avatar {
      box-shadow: 0 0 0 2px white, 0 0 0 4px #2563eb;
    }
    .name {
      color: #2563eb;
      font-weight: 500;
    }
  `}
  
  .avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    overflow: hidden;
  }
  
  .name {
    font-size: 12px;
    color: #6b7280;
    text-align: center;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const Dialog = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 50;
`

const DialogContent = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`

const DialogHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
`

const DialogTitle = styled.h3`
  font-weight: 600;
  font-size: 18px;
`

const DialogBody = styled.div`
  padding: 16px;
  max-height: 60vh;
  overflow-y: auto;
`

const DialogFooter = styled.div`
  padding: 16px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  
  ${(props) =>
    props.variant === "outline"
      ? `
    background-color: white;
    border: 1px solid #e5e7eb;
    
    &:hover {
      background-color: #f9fafb;
    }
  `
      : `
    background-color: #2563eb;
    border: 1px solid #2563eb;
    color: white;
    
    &:hover {
      background-color: #1d4ed8;
    }
  `}
`

const DatePickerButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background-color: white;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  
  &:hover {
    background-color: #f9fafb;
  }
`

const DatePickerPopover = styled.div`
  position: absolute;
  z-index: 1000;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
`

const TypeLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background-color: #f9fafb;
  }
`

const TypeColor = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
`

const ProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ProgressLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #475569;
  min-width: 80px;
`

const ProgressValue = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  min-width: 35px;
`

const ProgressBarContainer = styled.div`
  flex: 1 1 auto;
  height: 24px;
  min-height: 24px;
  max-height: 24px;
  background-color: #e8ebf0;
  border-radius: 4px;
  position: relative;
  overflow: hidden;
  border: 1px solid #d1d5db;
`

const BaselineProgressBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 50%;
  background-color: #fbbf24;
  opacity: 0.6;
  transition: width 0.2s;
  border-radius: 3px 0 0 0;
`

const ActualProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 50%;
  background-color: #3b82f6;
  opacity: 0.8;
  transition: width 0.2s;
  border-radius: 0 0 0 3px;
`

const TypeName = styled.span`
  font-size: 14px;
`

export default function Sidebar({
  tasks,
  users,
  milestones,
  activeTab,
  setActiveTab,
  selectedTask,
  setSelectedTask,
  addTask,
  updateTask,
  deleteTask,
  addMilestone,
  updateMilestone,
  deleteMilestone,
  setSelectedMilestone,
  selectedMilestone,
  taskTypes, // 작업 타입 props 추가
  reorderTasks,
  referenceDate,
  setReferenceDate,
  openBacklog, // 백로그(작업 목록) 모달 열기 콜백 - chartId 없으면 falsy
  openTaskNote, // 작업 노트 패널 열기 콜백 (task 객체 전달) - chartId 없으면 falsy
  addTaskNonce,
  addMilestoneNonce,
  onExport,
  onOpenLoadModal,
  searchQuery,
}) {

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isAddMilestoneDialogOpen, setIsAddMilestoneDialogOpen] = useState(false)

  useEffect(() => { if (addTaskNonce) setIsAddDialogOpen(true) }, [addTaskNonce])
  useEffect(() => { if (addMilestoneNonce) setIsAddMilestoneDialogOpen(true) }, [addMilestoneNonce])
// 날짜 선택 상태 추가 (기존 상태들과 함께)
const [showTaskStartDatePicker, setShowTaskStartDatePicker] = useState(false)
const [showTaskEndDatePicker, setShowTaskEndDatePicker] = useState(false)
  // 기본 작업 타입이 있으면 첫 번째 타입 ID를 사용, 없으면 빈 문자열
  const defaultTypeId = taskTypes && taskTypes.length > 0 ? taskTypes[0].id : "";
  // 기본 작업 타입의 색상을 가져오기
  const defaultColor = taskTypes && taskTypes.length > 0 ? taskTypes[0].color : "#4EA8DE";

  // 일정 기준 목표 진척률 자동 계산 (기준일이 시작/종료 사이일 때 경과 비율)
  const computeBaselineProgress = (startDate, endDate) => {
    if (!startDate || !endDate) return 0
    const start = new Date(startDate).getTime()
    const end = new Date(endDate).getTime()
    const ref = referenceDate ? new Date(referenceDate).getTime() : Date.now()
    if (isNaN(start) || isNaN(end) || end <= start) return 0
    if (ref <= start) return 0
    if (ref >= end) return 100
    return Math.round(((ref - start) / (end - start)) * 100)
  }

  const [newTask, setNewTask] = useState({
    name: "",
    startDate: new Date(),
    endDate: new Date(new Date().setDate(new Date().getDate() + 7)),
    progress: 0,
    baselineProgress: 0,
    dependencies: [],
    color: defaultColor,
    assignee: "",
    type: defaultTypeId, // 작업 타입 필드 추가
  })

  const [newMilestone, setNewMilestone] = useState({
    name: "",
    date: new Date(),
    color: "#F43F5E",
  })

  const [showStartDatePicker, setShowStartDatePicker] = useState(false)
  const [showEndDatePicker, setShowEndDatePicker] = useState(false)
  const [showMilestoneDatePicker, setShowMilestoneDatePicker] = useState(false)
 // 순서 변경 함수들
  const moveTaskUp = (taskId) => {
    const index = tasks.findIndex(t => t.id === taskId);
    if (index > 0) {
      const newTasks = [...tasks];
      [newTasks[index], newTasks[index - 1]] = [newTasks[index - 1], newTasks[index]];
      reorderTasks(newTasks);
    }
  };

  const moveTaskDown = (taskId) => {
    const index = tasks.findIndex(t => t.id === taskId);
    if (index < tasks.length - 1) {
      const newTasks = [...tasks];
      [newTasks[index], newTasks[index + 1]] = [newTasks[index + 1], newTasks[index]];
      reorderTasks(newTasks);
    }
  };
  
  const handleAddTask = () => {
    if (!newTask.name || !newTask.startDate || !newTask.endDate) return

    addTask({
      id: Date.now().toString(),
      name: newTask.name,
      startDate: newTask.startDate,
      endDate: newTask.endDate,
      progress: newTask.progress || 0,
      dependencies: newTask.dependencies || [],
      color: newTask.color || defaultColor,
      description: newTask.description,
      assignee: newTask.assignee,
      type: newTask.type, // 작업 타입 추가
    })

    setIsAddDialogOpen(false)
    setNewTask({
      name: "",
      startDate: new Date(),
      endDate: new Date(new Date().setDate(new Date().getDate() + 7)),
      progress: 0,
      dependencies: [],
      color: defaultColor,
      assignee: "",
      type: defaultTypeId,
    })
  }

  const handleAddMilestone = () => {
    if (!newMilestone.name || !newMilestone.date) return

    addMilestone({
      id: `m${Date.now()}`,
      name: newMilestone.name,
      date: newMilestone.date,
      color: newMilestone.color || "#F43F5E",
    })

    setIsAddMilestoneDialogOpen(false)
    setNewMilestone({
      name: "",
      date: new Date(),
      color: "#F43F5E",
    })
  }

  const selectedTaskData = tasks.find((task) => task.id === selectedTask)
  const selectedMilestoneData = milestones.find((m) => m.id === selectedMilestone)

  const updateSelectedTask = (field, value) => {
    if (!selectedTaskData) return

    const updatedTask = { ...selectedTaskData, [field]: value }
    updateTask(updatedTask)
  }

  // 선택된 작업의 타입 변경 함수
  const updateSelectedTaskType = (typeId) => {
    if (!selectedTaskData) return;
    
    const selectedType = taskTypes.find(type => type.id === typeId);
    updateTask({
      ...selectedTaskData,
      type: typeId,
      color: selectedType ? selectedType.color : selectedTaskData.color
    });
  };

  const updateSelectedMilestone = (field, value) => {
    if (!selectedMilestoneData) return

    const updatedMilestone = { ...selectedMilestoneData, [field]: value }
    updateMilestone(updatedMilestone)
  }

  // 작업 타입 이름 가져오기
  const getTaskTypeName = (typeId) => {
    if (!taskTypes) return "";
    const type = taskTypes.find(t => t.id === typeId);
    return type ? type.name : "";
  };

  return (
    <SidebarContainer>
      <SidebarHeader>
        {activeTab === "tasks" && (
          <PrimaryCTA onClick={() => setIsAddDialogOpen(true)}>
            <Plus size={16} strokeWidth={2.5} /> 새 작업
          </PrimaryCTA>
        )}
        {activeTab === "milestones" && (
          <PrimaryCTA onClick={() => setIsAddMilestoneDialogOpen(true)}>
            <Plus size={16} strokeWidth={2.5} /> 새 마일스톤
          </PrimaryCTA>
        )}
      </SidebarHeader>

      <TabContainer>
        <Tab active={activeTab === "tasks"} onClick={() => setActiveTab("tasks")}>
          작업
        </Tab>
        <Tab active={activeTab === "milestones"} onClick={() => setActiveTab("milestones")}>
          마일스톤
        </Tab>
      </TabContainer>

      {activeTab === "tasks" && openBacklog && (
        <BacklogLinkRow>
          <BacklogLink onClick={openBacklog}>
            <ListChecks size={13} /> 작업 목록에서 가져오기
          </BacklogLink>
        </BacklogLinkRow>
      )}

      <ContentContainer>
         {activeTab === "tasks" && (
    <TaskList>
      {tasks
        .filter((task) => {
          const q = (searchQuery || "").trim().toLowerCase()
          if (!q) return true
          const name = (task.name || "").toLowerCase()
          const typeName = (getTaskTypeName(task.type) || "").toLowerCase()
          const assignee = users.find((u) => u.id === task.assignee)
          const assigneeName = (assignee?.name || "").toLowerCase()
          return name.includes(q) || typeName.includes(q) || assigneeName.includes(q)
        })
        .map((task, index) => {
        const assignedUser = users.find((user) => user.id === task.assignee)
        const taskTypeName = getTaskTypeName(task.type);
        const status = getTaskStatus(task, referenceDate)

        return (
          <TaskItem
            key={task.id}
            isSelected={selectedTask === task.id}
            onClick={() => {
              setSelectedTask(task.id)
              setSelectedMilestone(null)
            }}
          >
            <TaskHeader>
              <TaskColor style={{ backgroundColor: task.color }} />
              <TaskName>{task.name}</TaskName>
              <StatusBadge bg={status.bg} color={status.color} border={status.border}>
                {status.label}
              </StatusBadge>
              {taskTypeName && (
                <div style={{
                  fontSize: '10px',
                  padding: '2px 6px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '10px',
                  flexShrink: 0,
                }}>
                  {taskTypeName}
                </div>
              )}
              
              {/* 순서 변경 버튼 추가 */}
              <OrderButtons>
                <OrderButton
                  onClick={(e) => {
                    e.stopPropagation();
                    moveTaskUp(task.id);
                  }}
                  disabled={index === 0}
                  title="위로 이동"
                >
                  <ArrowUp size={12} />
                </OrderButton>
                <OrderButton
                  onClick={(e) => {
                    e.stopPropagation();
                    moveTaskDown(task.id);
                  }}
                  disabled={index === tasks.length - 1}
                  title="아래로 이동"
                >
                  <ArrowDown size={12} />
                </OrderButton>
              </OrderButtons>

              {openTaskNote && (
                <NoteButton
                  onClick={(e) => { e.stopPropagation(); openTaskNote(task); }}
                  title="이 작업의 설명·참고자료 보기"
                  style={{ marginLeft: "auto" }}
                >
                  <HelpCircle size={12} /> 메모
                </NoteButton>
              )}

              {assignedUser && (
                <UserAvatar style={openTaskNote ? {} : { marginLeft: "auto" }}>
                  <img src={assignedUser.avatar || "/defaultImg.png"} alt={assignedUser.name} />
                </UserAvatar>
              )}
            </TaskHeader>
            <TaskDate>
              {format(task.startDate, "MMM d", { locale: ko })} -{" "}
              {format(task.endDate, "MMM d, yyyy", { locale: ko })}
            </TaskDate>
            <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: '#6b7280' }}>
              <span>목표: {computeBaselineProgress(task.startDate, task.endDate)}%</span>
              <span>실제: {task.progress}%</span>
              {(() => {
                if (Number(task.progress || 0) >= 100) return <span style={{ color: '#16a34a', fontWeight: 600 }}>완료</span>
                const ref = referenceDate ? new Date(referenceDate) : new Date()
                const refDay = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate())
                const start = task.startDate ? new Date(task.startDate) : null
                const end = task.endDate ? new Date(task.endDate) : null
                if (!start || !end) return null
                const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate())
                const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate())
                if (refDay < startDay) {
                  return <span style={{ color: '#9ca3af' }}>시작 D-{differenceInCalendarDays(startDay, refDay)}</span>
                }
                if (refDay > endDay) {
                  return <span style={{ color: '#ef4444', fontWeight: 600 }}>+{differenceInCalendarDays(refDay, endDay)}일 초과</span>
                }
                const d = differenceInCalendarDays(endDay, refDay)
                return <span style={{ color: d <= 3 ? '#ef4444' : d <= 7 ? '#f59e0b' : '#0f172a', fontWeight: 600 }}>D-{d}</span>
              })()}
            </div>
            <TaskProgressBar>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
                width: `${task.progress}%`,
                backgroundColor: status.color,
                opacity: 0.85,
                borderRadius: '3px 0 0 3px',
                transition: 'background-color 0.2s, width 0.2s',
              }} />
            </TaskProgressBar>
          </TaskItem>
        )
      })}
    </TaskList>
        )}

        {activeTab === "milestones" && (
          <div>
            {milestones
              .filter((m) => {
                const q = (searchQuery || "").trim().toLowerCase()
                if (!q) return true
                return (m.name || "").toLowerCase().includes(q)
              })
              .map((milestone) => (
              <MilestoneItem
                key={milestone.id}
                isSelected={selectedMilestone === milestone.id}
                onClick={() => {
                  setSelectedMilestone(milestone.id)
                  setSelectedTask(null)
                }}
              >
                <MilestoneMarker style={{ backgroundColor: milestone.color }}>★</MilestoneMarker>
                <MilestoneInfo>
                  <MilestoneName>{milestone.name}</MilestoneName>
                  <MilestoneDate>{format(milestone.date, "yyyy년 MM월 dd일", { locale: ko })}</MilestoneDate>
                </MilestoneInfo>
              </MilestoneItem>
            ))}
          </div>
        )}
      </ContentContainer>

      {(onOpenLoadModal || onExport) && (
        <SidebarFooter>
          {onOpenLoadModal && (
            <FooterButton onClick={onOpenLoadModal} title="저장된 차트 불러오기">
              <FolderOpen size={14} /> 불러오기
            </FooterButton>
          )}
          {onExport && (
            <FooterButton onClick={onExport} title="JSON으로 내보내기">
              <Download size={14} /> 내보내기
            </FooterButton>
          )}
        </SidebarFooter>
      )}

      {/* 작업 상세는 우측 push 패널(TaskDetailsPanel)로 이동 */}

      {selectedMilestoneData && (
        <TaskDetails>
          <TaskDetailsHeader>
            <TaskDetailsTitle>마일스톤 상세</TaskDetailsTitle>
            <HeaderActions>
              <CloseButton
                onClick={() => setSelectedMilestone(null)}
                title="닫기 (목록으로)"
              >
                <X size={16} />
              </CloseButton>
              <HeaderDivider />
              <DeleteButton
                onClick={async () => {
                  const ok = await confirmDialog(
                    `정말로 "${selectedMilestoneData.name}" 마일스톤을 삭제하시겠습니까?`
                  )
                  if (!ok) return
                  deleteMilestone(selectedMilestoneData.id)
                  setSelectedMilestone(null)
                }}
                title="마일스톤 삭제"
              >
                <Trash size={16} />
              </DeleteButton>
            </HeaderActions>
          </TaskDetailsHeader>

          <FormGroup>
            <Label htmlFor="edit-milestone-name">이름</Label>
            <Input
              id="edit-milestone-name"
              value={selectedMilestoneData.name}
              onChange={(e) => updateSelectedMilestone("name", e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <Label>날짜</Label>
            <DatePickerButton onClick={() => setShowMilestoneDatePicker(!showMilestoneDatePicker)}>
              <CalendarIcon size={16} />
              {format(selectedMilestoneData.date, "PPP", { locale: ko })}
            </DatePickerButton>

            {showMilestoneDatePicker && (
              <DatePickerPopover>
                <Calendar
                  mode="single"
                  selected={selectedMilestoneData.date}
                  onSelect={(date) => {
                    updateSelectedMilestone("date", date)
                    setShowMilestoneDatePicker(false)
                  }}
                />
              </DatePickerPopover>
            )}
          </FormGroup>

          <FormGroup>
            <Label>색상</Label>
            <ColorPicker>
              {["#F43F5E", "#8B5CF6", "#10B981", "#F59E0B", "#3B82F6"].map((color) => (
                <ColorOption
                  key={color}
                  style={{ backgroundColor: color }}
                  isSelected={selectedMilestoneData.color === color}
                  onClick={() => updateSelectedMilestone("color", color)}
                />
              ))}
            </ColorPicker>
          </FormGroup>
        </TaskDetails>
      )}

      {isAddDialogOpen && (
        <Dialog>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>새 작업 추가</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <FormGroup>
                <Label htmlFor="name">작업 이름</Label>
                <Input
                  id="name"
                  autoFocus
                  value={newTask.name}
                  onChange={(e) => setNewTask({ ...newTask, name: e.target.value })}
                />
              </FormGroup>

              

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                <FormGroup>
                  <Label>시작일</Label>
                  <div style={{ position: "relative" }}>
                    <DatePickerButton onClick={() => setShowStartDatePicker(!showStartDatePicker)}>
                      <CalendarIcon size={16} />
                      {format(newTask.startDate, "PPP", { locale: ko })}
                    </DatePickerButton>

                    {showStartDatePicker && (
                      <DatePickerPopover>
                        <Calendar
                          mode="single"
                          selected={newTask.startDate}
                          onSelect={(date) => {
                            setNewTask({ ...newTask, startDate: date })
                            setShowStartDatePicker(false)
                          }}
                        />
                      </DatePickerPopover>
                    )}
                  </div>
                </FormGroup>

                <FormGroup>
                  <Label>종료일</Label>
                  <div style={{ position: "relative" }}>
                    <DatePickerButton onClick={() => setShowEndDatePicker(!showEndDatePicker)}>
                      <CalendarIcon size={16} />
                      {format(newTask.endDate, "PPP", { locale: ko })}
                    </DatePickerButton>

                    {showEndDatePicker && (
                      <DatePickerPopover>
                        <Calendar
                          mode="single"
                          selected={newTask.endDate}
                          onSelect={(date) => {
                            setNewTask({ ...newTask, endDate: date })
                            setShowEndDatePicker(false)
                          }}
                        />
                      </DatePickerPopover>
                    )}
                  </div>
                </FormGroup>
              </div>
              <FormGroup>
               <Label>진행률</Label>
               <ProgressSection>

                 <ProgressRow>
                   <ProgressLabel>실제:</ProgressLabel>
                   <NumberInput
                     value={newTask.progress}
                     min={0}
                     max={100}
                     onChange={(e) => setNewTask({ ...newTask, progress: Number.parseInt(e.target.value) })}
                   />
                   <ProgressValue>{newTask.progress}%</ProgressValue>
                   <SliderContainer style={{ flex: 1 }}>
                     <Slider
                       value={newTask.progress}
                       min={0}
                       max={100}
                       step={5}
                       onChange={(e) => setNewTask({ ...newTask, progress: Number.parseInt(e.target.value) })}
                     />
                   </SliderContainer>
                 </ProgressRow>
               </ProgressSection>
             </FormGroup>

             <FormGroup>
               <Label>색상</Label>
               <ColorPicker>
                 {taskTypes && taskTypes.length > 0 ? (
                   // 작업 타입에서 가져온 색상들 표시
                   taskTypes.map((type) => (
                     <ColorOption
                       key={type.color}
                       style={{ backgroundColor: type.color }}
                       isSelected={newTask.color === type.color}
                       onClick={() => setNewTask({ ...newTask, color: type.color })}
                     />
                   ))
                 ) : (
                   // 기본 색상 팔레트
                   ["#4EA8DE", "#8884D8", "#82CA9D", "#FFBB28", "#FF8042"].map((color) => (
                     <ColorOption
                       key={color}
                       style={{ backgroundColor: color }}
                       isSelected={newTask.color === color}
                       onClick={() => setNewTask({ ...newTask, color })}
                     />
                   ))
                 )}
               </ColorPicker>
             </FormGroup>

             <FormGroup>
               <Label>담당자</Label>
               <UserSelector>
                 {users.map((user) => (
                   <UserOption
                     key={user.id}
                     isSelected={newTask.assignee === user.id}
                     onClick={() => setNewTask({ ...newTask, assignee: user.id })}
                   >
                     <div className="avatar">
                       <img src={user.avatar || "/placeholder.svg"} alt={user.name} />
                     </div>
                     <div className="name">{user.name}</div>
                   </UserOption>
                 ))}
               </UserSelector>
             </FormGroup>

             <FormGroup>
               <Label>이전 작업 연결</Label>
               <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                 {tasks.map((task) => (
                   <label key={task.id} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                     <input
                       type="checkbox"
                       checked={(newTask.dependencies || []).includes(task.id)}
                       onChange={(e) => {
                         const deps = newTask.dependencies || []
                         if (e.target.checked) {
                           setNewTask({ ...newTask, dependencies: [...deps, task.id] })
                         } else {
                           setNewTask({
                             ...newTask,
                             dependencies: deps.filter((id) => id !== task.id),
                           })
                         }
                       }}
                     />
                     {task.name}
                   </label>
                 ))}
               </div>
             </FormGroup>
           </DialogBody>
           <DialogFooter>
             <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
               취소
             </Button>
             <Button onClick={handleAddTask}>추가</Button>
           </DialogFooter>
         </DialogContent>
       </Dialog>
     )}

     {isAddMilestoneDialogOpen && (
       <Dialog>
         <DialogContent>
           <DialogHeader>
             <DialogTitle>새 마일스톤 추가</DialogTitle>
           </DialogHeader>
           <DialogBody>
             <FormGroup>
               <Label htmlFor="milestone-name">마일스톤 이름</Label>
               <Input
                 id="milestone-name"
                 autoFocus
                 value={newMilestone.name}
                 onChange={(e) => setNewMilestone({ ...newMilestone, name: e.target.value })}
               />
             </FormGroup>

             <FormGroup>
               <Label>날짜</Label>
               <div style={{ position: "relative" }}>
                 <DatePickerButton onClick={() => setShowMilestoneDatePicker(!showMilestoneDatePicker)}>
                   <CalendarIcon size={16} />
                   {format(newMilestone.date, "PPP", { locale: ko })}
                 </DatePickerButton>

                 {showMilestoneDatePicker && (
                   <DatePickerPopover>
                     <Calendar
                       mode="single"
                       selected={newMilestone.date}
                       onSelect={(date) => {
                         setNewMilestone({ ...newMilestone, date })
                         setShowMilestoneDatePicker(false)
                       }}
                     />
                   </DatePickerPopover>
                 )}
               </div>
             </FormGroup>

             <FormGroup>
               <Label>색상</Label>
               <ColorPicker>
                 {["#F43F5E", "#8B5CF6", "#10B981", "#F59E0B", "#3B82F6"].map((color) => (
                   <ColorOption
                     key={color}
                     style={{ backgroundColor: color }}
                     isSelected={newMilestone.color === color}
                     onClick={() => setNewMilestone({ ...newMilestone, color })}
                   />
                 ))}
               </ColorPicker>
             </FormGroup>
           </DialogBody>
           <DialogFooter>
             <Button variant="outline" onClick={() => setIsAddMilestoneDialogOpen(false)}>
               취소
             </Button>
             <Button onClick={handleAddMilestone}>추가</Button>
           </DialogFooter>
         </DialogContent>
       </Dialog>
     )}
   </SidebarContainer>
 )
}